import { Icon } from '@/components/ui/Icon';
import {
  mdiAccount,
  mdiFilter,
  mdiMagnify,
  mdiMonitor,
  mdiStore,
  mdiTag,
} from '@mdi/js';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

interface Command {
  id: string;
  label: string;
  description?: string;
  icon: string;
  action: () => void;
  shortcut?: string;
  category: 'navigation' | 'action' | 'search';
}

export interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CommandPalette: React.FC<CommandPaletteProps> = ({
  isOpen,
  onClose,
}) => {
  const [query, setQuery] = React.useState('');
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const navigate = useNavigate();

  const baseCommands: Command[] = [
    // Navigation commands
    {
      id: 'nav-dashboard',
      label: 'Go to Dashboard',
      description: 'Navigate to the main dashboard',
      icon: mdiMonitor,
      action: () => {
        navigate('/dashboard');
        onClose();
      },
      shortcut: '⌘D',
      category: 'navigation',
    },
    {
      id: 'nav-users',
      label: 'Go to Users',
      description: 'Manage users and permissions',
      icon: mdiAccount,
      action: () => {
        navigate('/users');
        onClose();
      },
      shortcut: '⌘U',
      category: 'navigation',
    },
    {
      id: 'nav-businesses',
      label: 'Go to Businesses',
      description: 'Manage business listings',
      icon: mdiStore,
      action: () => {
        navigate('/businesses');
        onClose();
      },
      shortcut: '⌘B',
      category: 'navigation',
    },
    {
      id: 'nav-categories',
      label: 'Go to Categories',
      description: 'Manage business categories',
      icon: mdiTag,
      action: () => {
        navigate('/categories');
        onClose();
      },
      shortcut: '⌘C',
      category: 'navigation',
    },
    {
      id: 'nav-filters',
      label: 'Go to Filters',
      description: 'Manage business filters',
      icon: mdiFilter,
      action: () => {
        navigate('/filters');
        onClose();
      },
      shortcut: '⌘F',
      category: 'navigation',
    },
  ];

  const filteredCommands = baseCommands.filter(
    command =>
      command.label.toLowerCase().includes(query.toLowerCase()) ||
      command.description?.toLowerCase().includes(query.toLowerCase()),
  );

  // Reset selected index when query changes
  React.useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Handle keyboard navigation
  const handleKeyDown = React.useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev < filteredCommands.length - 1 ? prev + 1 : 0,
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev =>
            prev > 0 ? prev - 1 : filteredCommands.length - 1,
          );
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [isOpen, filteredCommands, selectedIndex, onClose],
  );

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Reset query when palette opens/closes
  React.useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const paletteContent = (
    <div className='fixed inset-0 z-50 flex items-start justify-center pt-20 px-4'>
      {/* Backdrop */}
      <div
        className='absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm'
        onClick={onClose}
      />

      {/* Command palette */}
      <div className='relative w-full max-w-lg bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden'>
        {/* Search input */}
        <div className='flex items-center px-4 py-4 border-b border-gray-200'>
          <Icon path={mdiMagnify} size={20} className='text-gray-400 mr-3' />
          <input
            type='text'
            placeholder='Search commands or type a command...'
            value={query}
            onChange={e => setQuery(e.target.value)}
            className='flex-1 bg-transparent text-gray-900 placeholder-gray-500 focus:outline-none text-lg'
            autoFocus
          />
          <div className='ml-3 text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded'>
            ESC
          </div>
        </div>

        {/* Commands list */}
        <div className='max-h-96 overflow-y-auto'>
          {filteredCommands.length === 0 ? (
            <div className='px-4 py-8 text-center text-gray-500'>
              <Icon
                path={mdiMagnify}
                size={24}
                className='mx-auto mb-2 opacity-50'
              />
              <p>No commands found</p>
              <p className='text-sm mt-1'>Try a different search term</p>
            </div>
          ) : (
            <div className='py-2'>
              {filteredCommands.map((command, index) => (
                <button
                  key={command.id}
                  onClick={() => command.action()}
                  className={`
                    w-full px-4 py-3 text-left flex items-center space-x-3 transition-colors
                    ${
                      index === selectedIndex
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-900 hover:bg-gray-50'
                    }
                  `}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <Icon
                    path={command.icon}
                    size={20}
                    className={
                      index === selectedIndex
                        ? 'text-blue-500'
                        : 'text-gray-400'
                    }
                  />
                  <div className='flex-1'>
                    <div className='font-medium'>{command.label}</div>
                    {command.description && (
                      <div
                        className={`text-sm ${
                          index === selectedIndex
                            ? 'text-blue-600'
                            : 'text-gray-500'
                        }`}
                      >
                        {command.description}
                      </div>
                    )}
                  </div>
                  {command.shortcut && (
                    <div
                      className={`text-xs px-2 py-1 rounded ${
                        index === selectedIndex
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {command.shortcut}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className='px-4 py-3 border-t border-gray-200 bg-gray-50'>
          <div className='flex items-center justify-between text-xs text-gray-500'>
            <div className='flex items-center space-x-4'>
              <div className='flex items-center space-x-1'>
                <div className='px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs'>
                  ↑↓
                </div>
                <span>Navigate</span>
              </div>
              <div className='flex items-center space-x-1'>
                <div className='px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs'>
                  ↵
                </div>
                <span>Select</span>
              </div>
            </div>
            <div className='flex items-center space-x-1'>
              <div className='px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs'>
                ESC
              </div>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(paletteContent, document.body)
    : null;
};
